import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

// Header Component
function Header() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-0 ${
        scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm py-3 md:py-4' : 'bg-transparent py-4 md:py-6'
      }`}
    >
      <div className="w-full px-4 md:px-6 lg:px-16 flex items-center justify-between">
        <Link to="/" className="cursor-pointer">
          <img
            src="https://static.readdy.ai/image/45f36db358624bab4161a57e418814dc/952de5dd7ee479a30999c64f6b1ab29c.png"
            alt="GO TALENTS"
            className="h-10 md:h-14 lg:h-16 w-auto object-contain transition-all"
          />
        </Link>

        <a
          href="#contact-form"
          className="px-3 md:px-6 py-2 md:py-2.5 text-[10px] md:text-xs font-semibold tracking-wider rounded-full transition-all cursor-pointer whitespace-nowrap bg-[#F49729] text-white hover:bg-[#e08820]"
        >
          <span className="inline sm:hidden">REQUEST</span>
          <span className="hidden sm:inline">REQUEST TALENT</span>
        </a>
      </div>
    </header>
  );
}

// Hero Section
function HeroSection() {
  return (
    <section className="pt-24 md:pt-32 pb-12 md:pb-24 px-4 md:px-6 lg:px-16 bg-[#1a1a1a] relative overflow-hidden">
      <div className="absolute top-20 right-20 w-96 h-96 bg-[#F49729]/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-10 left-10 w-64 h-64 bg-[#F49729]/5 rounded-full blur-3xl"></div>

      <div className="max-w-6xl mx-auto text-center relative z-10">
        <p className="text-[#F49729] text-[10px] md:text-xs font-semibold tracking-[0.25em] uppercase mb-3 md:mb-4">
          Hire Top Talent
        </p>
        <h1 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white leading-tight mb-4 md:mb-6">
          Find Your
          <br />
          <span className="text-[#F49729]">Accounting Team</span>
        </h1>
      </div>

      {/* Bottom curved separator */}
      <div className="absolute bottom-[-2px] left-0 right-0 z-10">
        <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" className="w-full block" style={{ height: '40px', display: 'block' }}>
          <path d="M0 60L0 30C360 0 720 10 1080 30C1260 40 1380 50 1440 45L1440 60L0 60Z" fill="white"/>
        </svg>
      </div>
    </section>
  );
}

// Services Cards
const services = [
  {
    icon: 'ri-time-line',
    title: 'Flexible Hiring Models',
    description: 'Full-time · Part-time · Project-based',
  },
  {
    icon: 'ri-medal-line',
    title: 'All-Level Talent Hiring',
    description: 'Junior · Semi-Senior · Senior',
  },
  {
    icon: 'ri-group-2-line',
    title: 'Role-Matched Hiring',
    description: 'Candidates matched just to your specific opening',
  },
];

function ServicesCards() {
  return (
    <section id="services-cards" className="py-16 md:py-24 px-4 md:px-6 lg:px-16 bg-white relative overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12 md:mb-16 lg:mb-20">
          <p className="text-[#F49729] text-xs md:text-sm font-semibold tracking-[0.25em] uppercase mb-3 md:mb-5">
            Our Services
          </p>
          <h2 className="text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold text-[#111111] leading-tight">
            Tailored Hiring Solutions
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-6 md:gap-8 lg:gap-10">
          {services.map((service, index) => (
            <div
              key={index}
              className="group relative p-6 md:p-10 lg:p-12 rounded-3xl bg-white border border-gray-100 hover:border-[#F49729]/30 transition-all duration-300 hover:shadow-2xl hover:shadow-[#F49729]/10 cursor-default text-center"
            >
              <div className="w-14 h-14 md:w-20 md:h-20 lg:w-24 lg:h-24 flex items-center justify-center rounded-2xl bg-gradient-to-br from-[#F49729]/10 to-[#F49729]/5 text-[#F49729] mb-6 md:mb-8 lg:mb-10 mx-auto group-hover:from-[#F49729] group-hover:to-[#e08820] group-hover:text-white transition-all duration-300">
                <i className={`${service.icon} text-2xl md:text-3xl lg:text-4xl`}></i>
              </div>
              <h3 className="text-lg md:text-xl lg:text-2xl font-semibold text-[#111111] mb-3 md:mb-4">
                {service.title}
              </h3>
              <p className="text-sm md:text-base text-[#61616A] leading-relaxed">
                {service.description}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom curved separator */}
      <div className="absolute bottom-[-2px] left-0 right-0 z-10">
        <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" className="w-full block" style={{ height: '40px', display: 'block' }}>
          <path d="M0 60L0 30C360 0 720 10 1080 30C1260 40 1380 50 1440 45L1440 60L0 60Z" fill="#faf9f7"/>
        </svg>
      </div>
    </section>
  );
}

// Cost Savings Section
function CostSavings() {
  return (
    <section id="cost-savings" className="py-14 md:py-24 lg:py-28 px-4 md:px-6 lg:px-16 bg-[#faf9f7] relative overflow-hidden">
      <div className="absolute -top-40 -right-40 w-96 h-96 bg-[#F49729]/10 rounded-full blur-3xl"></div>
      <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-[#F49729]/10 rounded-full blur-3xl"></div>

      <div className="max-w-5xl mx-auto relative z-10">
        <div className="w-12 h-12 md:w-16 md:h-16 flex items-center justify-center rounded-full bg-gradient-to-br from-[#F49729] to-[#e08820] text-white mx-auto mb-6 md:mb-8">
          <i className="ri-money-dollar-circle-line text-xl md:text-2xl"></i>
        </div>
        <p className="text-[#F49729] text-[10px] md:text-xs font-semibold tracking-[0.25em] uppercase mb-4 md:mb-6 text-center">
          Cost Savings
        </p>

        <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-center bg-white rounded-[24px] md:rounded-[40px] border border-gray-100 shadow-[0_20px_60px_-15px_rgba(244,151,41,0.15)] p-8 md:p-14 lg:p-16">
          {/* Giant stat */}
          <div className="text-center md:text-left">
            <p className="text-[5.5rem] md:text-[7rem] lg:text-[8rem] font-black text-[#F49729] leading-[0.85] tracking-tighter">
              50%
            </p>
            <p className="text-xl md:text-2xl font-bold text-[#111111] mt-2">Lower Cost</p>
            <p className="text-sm md:text-base text-[#61616A] mt-3 leading-relaxed">
              Lower cost than hiring locally, same quality of work.
            </p>
          </div>

          {/* Comparison bars */}
          <div className="space-y-7 md:space-y-8">
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs md:text-sm font-semibold text-[#111111]">Hiring Locally</span>
                <span className="text-xs md:text-sm font-semibold text-[#61616A]">100%</span>
              </div>
              <div className="h-3 md:h-4 bg-gray-100 rounded-full w-full overflow-hidden">
                <div className="h-full bg-gray-300 rounded-full w-full"></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs md:text-sm font-semibold text-[#111111]">With GO TALENTS</span>
                <span className="text-xs md:text-sm font-bold text-[#F49729]">Up to 50%</span>
              </div>
              <div className="h-3 md:h-4 bg-gray-100 rounded-full w-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[#F49729] to-[#e08820] rounded-full w-1/2"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom curved separator */}
      <div className="absolute bottom-[-2px] left-0 right-0 z-10">
        <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" className="w-full block" style={{ height: '40px', display: 'block' }}>
          <path d="M0 60L0 30C360 0 720 10 1080 30C1260 40 1380 50 1440 45L1440 60L0 60Z" fill="#1a1a1a"/>
        </svg>
      </div>
    </section>
  );
}

// Exclusivity Section
function Exclusivity() {
  return (
    <section id="exclusivity" className="py-12 md:py-20 px-4 md:px-6 lg:px-16 bg-[#1a1a1a] relative overflow-hidden">
      <div className="absolute top-20 right-20 w-64 h-64 bg-[#F49729]/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-10 left-20 w-48 h-48 bg-[#F49729]/5 rounded-full blur-2xl"></div>

      <div className="max-w-4xl mx-auto text-center relative z-10 py-8 md:py-10">
        <div className="w-12 h-12 md:w-16 md:h-16 flex items-center justify-center rounded-full bg-gradient-to-br from-[#F49729] to-[#e08820] text-white mx-auto mb-6 md:mb-8">
          <i className="ri-shield-check-line text-xl md:text-2xl"></i>
        </div>
        <p className="text-[#F49729] text-[10px] md:text-xs font-semibold tracking-[0.25em] uppercase mb-4 md:mb-6">
          100% Exclusivity
        </p>
        <h2 className="text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold text-white leading-tight mb-4 md:mb-6">
          Yes. Your talents work
          <br />
          <span className="text-[#F49729]">100% exclusively</span> for your company.
        </h2>
        <p className="text-sm md:text-base text-white/60 max-w-2xl mx-auto">
          We guarantee that every professional we place is dedicated solely to your organization, ensuring full commitment and alignment with your goals.
        </p>
      </div>

      {/* Bottom curved separator */}
      <div className="absolute bottom-[-2px] left-0 right-0 z-10">
        <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" className="w-full block" style={{ height: '40px', display: 'block' }}>
          <path d="M0 60L0 30C360 0 720 10 1080 30C1260 40 1380 50 1440 45L1440 60L0 60Z" fill="#faf9f7"/>
        </svg>
      </div>
    </section>
  );
}

// Video Section
function VideoSection() {
  const [isPlaying, setIsPlaying] = useState(false);

  return (
    <section id="video" className="py-12 md:py-20 lg:py-24 px-4 md:px-6 lg:px-16 bg-[#faf9f7] relative overflow-hidden">
      <div className="absolute -top-40 right-0 w-96 h-96 bg-[#F49729]/5 rounded-full blur-3xl"></div>

      <div className="max-w-5xl mx-auto relative z-10">
        <div className="text-center mb-8 md:mb-12">
          <p className="text-[#F49729] text-[10px] md:text-xs font-semibold tracking-[0.25em] uppercase mb-3 md:mb-4">
            See It In Action
          </p>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#111111] leading-tight">
            How We Work
          </h2>
        </div>

        <div className="relative rounded-2xl md:rounded-3xl overflow-hidden shadow-2xl shadow-black/10">
          <div className="aspect-video bg-[#1a1a1a] relative">
            {!isPlaying ? (
              <>
                <img
                  src="https://img.youtube.com/vi/ixZqxE1fwio/maxresdefault.jpg"
                  alt="Video thumbnail"
                  className="w-full h-full object-cover object-top opacity-80"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                <button
                  onClick={() => setIsPlaying(true)}
                  className="absolute inset-0 flex items-center justify-center group cursor-pointer"
                >
                  <div className="w-14 h-14 md:w-20 md:h-20 lg:w-24 lg:h-24 flex items-center justify-center rounded-full bg-white/90 text-[#F49729] group-hover:bg-[#F49729] group-hover:text-white transition-all duration-300 shadow-2xl">
                    <i className="ri-play-fill text-2xl md:text-3xl lg:text-4xl ml-1"></i>
                  </div>
                </button>
                <div className="absolute bottom-4 md:bottom-8 left-4 md:left-8 right-4 md:right-8">
                </div>
              </>
            ) : (
              <iframe
                src="https://www.youtube.com/embed/ixZqxE1fwio?autoplay=1"
                className="w-full h-full"
                allow="autoplay; encrypted-media"
                allowFullScreen
                title="GO TALENTS Recruitment Process Video"
              ></iframe>
            )}
          </div>
        </div>
      </div>

      {/* Bottom curved separator */}
      <div className="absolute bottom-[-2px] left-0 right-0 z-10">
        <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" className="w-full block" style={{ height: '40px', display: 'block' }}>
          <path d="M0 60L0 30C360 0 720 10 1080 30C1260 40 1380 50 1440 45L1440 60L0 60Z" fill="white"/>
        </svg>
      </div>
    </section>
  );
}

// Industries Section
const industries = [
  { icon: 'ri-book-2-line', name: 'Bookkeeping' },
  { icon: 'ri-bank-card-line', name: 'Payroll Processing' },
  { icon: 'ri-file-text-line', name: 'Tax Preparation' },
  { icon: 'ri-bar-chart-box-line', name: 'Financial Reporting' },
  { icon: 'ri-shield-check-line', name: 'Audit Support' },
  { icon: 'ri-line-chart-line', name: 'Client Accounting Services' },
];

function Industries() {
  return (
    <section id="industries" className="py-16 md:py-20 lg:py-24 px-4 md:px-6 lg:px-16 bg-white relative overflow-hidden">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10 md:mb-12 lg:mb-16">
          <p className="text-[#F49729] text-[10px] md:text-xs font-semibold tracking-[0.25em] uppercase mb-3 md:mb-4">
            What We Cover
          </p>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#111111] leading-tight">
            Every Corner of Your Books
          </h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6">
          {industries.map((industry, index) => (
            <div
              key={index}
              className="group p-4 md:p-6 rounded-2xl bg-[#faf9f7] hover:bg-gradient-to-br hover:from-[#F49729] hover:to-[#e08820] transition-all duration-300 text-center cursor-default"
            >
              <div className="w-10 h-10 md:w-14 md:h-14 flex items-center justify-center rounded-xl bg-white text-[#F49729] mx-auto mb-3 md:mb-4 group-hover:bg-white/20 group-hover:text-white transition-all duration-300">
                <i className={`${industry.icon} text-xl md:text-2xl`}></i>
              </div>
              <p className="text-xs md:text-sm font-medium text-[#111111] group-hover:text-white transition-all duration-300">
                {industry.name}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Final CTA Section with Contact Form
function FinalCTA() {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    company: '',
    industry: '',
    hiringType: '',
    experienceLevel: '',
    description: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const formDataEncoded = new URLSearchParams();
      formDataEncoded.append('fullName', formData.fullName);
      formDataEncoded.append('email', formData.email);
      formDataEncoded.append('company', formData.company);
      formDataEncoded.append('industry', formData.industry);
      formDataEncoded.append('hiringType', formData.hiringType);
      formDataEncoded.append('experienceLevel', formData.experienceLevel);
      formDataEncoded.append('description', formData.description);
      
formDataEncoded.append('form_type', 'hire');
      await fetch('/contact.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formDataEncoded.toString(),
      });
      setSubmitted(true);
    } catch (err) {
      console.error('Form submission failed:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact-form" className="py-16 md:py-20 lg:py-24 px-4 md:px-6 lg:px-16 bg-[#1a1a1a] relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 z-10 leading-none">
        <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full block" style={{ display: 'block', marginBottom: '-1px' }}>
          <path d="M0 0L0 30C360 60 720 50 1080 40C1260 30 1380 20 1440 30L1440 0L0 0Z" fill="#ffffff"/>
        </svg>
      </div>

      <div className="absolute top-20 right-20 w-80 h-80 bg-[#F49729]/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 left-20 w-64 h-64 bg-[#F49729]/5 rounded-full blur-3xl"></div>

      <div className="max-w-4xl mx-auto relative z-10 pt-8 md:pt-10">
        <div className="text-center mb-8 md:mb-12">
          <p className="text-[#F49729] text-[10px] md:text-xs font-semibold tracking-[0.25em] uppercase mb-4 md:mb-6">
            Ready to Start?
          </p>
          <h2 className="text-2xl md:text-3xl lg:text-4xl xl:text-5xl 2xl:text-6xl font-bold text-white leading-tight mb-4 md:mb-6">
            Let&apos;s Build Your
            <br />
            <span className="text-[#F49729]">Dream Team</span>
          </h2>
          <p className="text-sm md:text-base lg:text-lg text-white/60 max-w-2xl mx-auto">
            Fill out the form below and our team will get back to you within 24 hours with tailored talent solutions.
          </p>
        </div>

        {submitted ? (
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl md:rounded-3xl p-8 md:p-12 text-center">
            <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-[#F49729] text-white mx-auto mb-4 md:mb-6">
              <i className="ri-check-line text-3xl md:text-4xl"></i>
            </div>
            <h3 className="text-xl md:text-2xl font-bold text-white mb-2 md:mb-3">Thank You!</h3>
            <p className="text-sm md:text-base text-white/60">
              We&apos;ve received your request. Our team will contact you within 24 hours.
            </p>
          </div>
        ) : (
          <form
            id="talent-request-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="bg-white/10 backdrop-blur-sm rounded-2xl md:rounded-3xl p-5 md:p-8 lg:p-12"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-4 md:mb-6">
              <div>
                <label htmlFor="fullName" className="block text-xs md:text-sm font-medium text-white mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  required
                  placeholder="John Smith"
                  className="w-full px-4 md:px-5 py-3.5 md:py-4 text-sm bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#F49729]/50 focus:border-[#F49729] transition-all"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-xs md:text-sm font-medium text-white mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="john@company.com"
                  className="w-full px-4 md:px-5 py-3.5 md:py-4 text-sm bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#F49729]/50 focus:border-[#F49729] transition-all"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-4 md:mb-6">
              <div>
                <label htmlFor="company" className="block text-xs md:text-sm font-medium text-white mb-2">
                  Company Name *
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  required
                  placeholder="Your Company Inc."
                  className="w-full px-4 md:px-5 py-3.5 md:py-4 text-sm bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#F49729]/50 focus:border-[#F49729] transition-all"
                />
              </div>
              <div>
                <label htmlFor="industry" className="block text-xs md:text-sm font-medium text-white mb-2">
                  Area *
                </label>
                <select
                  id="industry"
                  name="industry"
                  value={formData.industry}
                  onChange={handleChange}
                  required
                  className="w-full px-4 md:px-5 py-3.5 md:py-4 text-sm bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#F49729]/50 focus:border-[#F49729] transition-all cursor-pointer appearance-none"
                  style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='white'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 1rem center', backgroundSize: '1.25rem' }}
                >
                  <option value="" className="bg-[#1a1a1a] text-white">Select area</option>
                  <option value="Accounting" className="bg-[#1a1a1a] text-white">Accounting</option>
                  <option value="Bookkeeping" className="bg-[#1a1a1a] text-white">Bookkeeping</option>
                  <option value="Other" className="bg-[#1a1a1a] text-white">Other</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-4 md:mb-6">
              <div>
                <label htmlFor="hiringType" className="block text-xs md:text-sm font-medium text-white mb-2">
                  Hiring Type *
                </label>
                <select
                  id="hiringType"
                  name="hiringType"
                  value={formData.hiringType}
                  onChange={handleChange}
                  required
                  className="w-full px-4 md:px-5 py-3.5 md:py-4 text-sm bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#F49729]/50 focus:border-[#F49729] transition-all cursor-pointer appearance-none"
                  style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='white'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 1rem center', backgroundSize: '1.25rem' }}
                >
                  <option value="" className="bg-[#1a1a1a] text-white">Select type</option>
                  <option value="Full-time" className="bg-[#1a1a1a] text-white">Full-time</option>
                  <option value="Part-time" className="bg-[#1a1a1a] text-white">Part-time</option>
                  <option value="Project-based" className="bg-[#1a1a1a] text-white">Project-based</option>
                </select>
              </div>
              <div>
                <label htmlFor="experienceLevel" className="block text-xs md:text-sm font-medium text-white mb-2">
                  Experience Level *
                </label>
                <select
                  id="experienceLevel"
                  name="experienceLevel"
                  value={formData.experienceLevel}
                  onChange={handleChange}
                  required
                  className="w-full px-4 md:px-5 py-3.5 md:py-4 text-sm bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#F49729]/50 focus:border-[#F49729] transition-all cursor-pointer appearance-none"
                  style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='white'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 1rem center', backgroundSize: '1.25rem' }}
                >
                  <option value="" className="bg-[#1a1a1a] text-white">Select level</option>
                  <option value="Junior" className="bg-[#1a1a1a] text-white">Junior (0-2 years)</option>
                  <option value="Semi-Senior" className="bg-[#1a1a1a] text-white">Semi-Senior (2-5 years)</option>
                  <option value="Senior" className="bg-[#1a1a1a] text-white">Senior (5+ years)</option>
                  <option value="Mixed" className="bg-[#1a1a1a] text-white">Mixed levels</option>
                </select>
              </div>
            </div>

            <div className="mb-6 md:mb-8">
              <label htmlFor="description" className="block text-xs md:text-sm font-medium text-white mb-2">
                Role Description (optional)
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                maxLength={500}
                rows={4}
                placeholder="Describe the role, key responsibilities, required skills, and any other relevant details..."
                className="w-full px-4 md:px-5 py-3.5 md:py-4 text-sm bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#F49729]/50 focus:border-[#F49729] transition-all resize-none"
              ></textarea>
              <p className="text-xs text-white/40 mt-2 text-right">
                {formData.description.length}/500 characters
              </p>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-4 text-sm font-semibold tracking-wider text-white bg-[#F49729] rounded-xl hover:bg-[#e08820] transition-all cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  SUBMITTING...
                </>
              ) : (
                <>
                  SUBMIT REQUEST
                  <i className="ri-arrow-right-line"></i>
                </>
              )}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}

// Footer Component
function Footer() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      const formData = new URLSearchParams();
      formData.append('email', email);
      formData.append('form_type', 'newsletter');
      await fetch('/contact.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });
      setSubmitted(true);
      setEmail('');
    } catch (err) {
      console.error('Newsletter subscription failed:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer className="relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 z-10">
        <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
          <path d="M0 0L0 40C360 80 720 60 1080 40C1260 30 1380 20 1440 30L1440 0L0 0Z" fill="#1a1a1a" />
        </svg>
      </div>

      <div className="bg-[#f5f0eb] pt-20 md:pt-24 pb-6 md:pb-8 px-4 md:px-6 lg:px-16">
        <div className="absolute top-20 right-20 w-64 h-64 bg-[#F49729]/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-20 w-48 h-48 bg-[#F49729]/5 rounded-full blur-3xl"></div>

        <div className="max-w-6xl mx-auto relative z-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-7 md:gap-10 mb-10 md:mb-14">
            <div className="col-span-2 md:col-span-1">
              <Link to="/">
                <img
                  src="https://static.readdy.ai/image/45f36db358624bab4161a57e418814dc/952de5dd7ee479a30999c64f6b1ab29c.png"
                  alt="GO TALENTS"
                  className="h-12 md:h-20 w-auto object-contain mb-4 md:mb-5 cursor-pointer"
                />
              </Link>
            </div>

            <div>
              <h4 className="text-xs font-semibold text-[#111111] uppercase tracking-wider mb-4 md:mb-5">
                <a href="/">Quick Links</a>
              </h4>
              <div className="space-y-3">
                <Link to="/hire-talent" className="block text-sm text-[#61616A] hover:text-[#F49729] transition-colors cursor-pointer">
                  Hire Talent
                </Link>
                <Link to="/apply-talent" className="block text-sm text-[#61616A] hover:text-[#F49729] transition-colors cursor-pointer">
                  Apply as Talent
                </Link>
                <Link to="/privacy" className="block text-sm text-[#61616A] hover:text-[#F49729] transition-colors cursor-pointer">
                  Privacy Policy
                </Link>
              </div>
            </div>

            <div>
              <h4 className="text-xs font-semibold text-[#111111] uppercase tracking-wider mb-4 md:mb-5">
                <a href="mailto:contact@gotalentsglobal.com">Contact</a>
              </h4>
              <a
                href="mailto:contact@gotalentsglobal.com"
                className="block text-sm text-[#61616A] hover:text-[#F49729] transition-colors mb-4 cursor-pointer break-all"
              >
                contact@<wbr />gotalentsglobal.com
              </a>
              <div className="flex items-center gap-3">
                <a
                  href="https://www.linkedin.com/company/go-talentsglobal"
                  rel="nofollow"
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-white text-[#61616A] hover:text-[#F49729] hover:shadow-lg transition-all cursor-pointer"
                >
                  <i className="ri-linkedin-fill text-base"></i>
                </a>
              </div>
            </div>

            <div className="col-span-2 md:col-span-1">
              <h4 className="text-xs font-semibold text-[#111111] uppercase tracking-wider mb-3 md:mb-5">Newsletter</h4>
              <p className="text-sm text-[#61616A] mb-3 md:mb-4">
                Stay updated with the latest opportunities and insights.
              </p>
              {submitted ? (
                <div className="bg-[#F49729]/10 rounded-2xl p-4">
                  <p className="text-sm text-[#F49729] font-medium">Thank you for subscribing!</p>
                </div>
              ) : (
                <form
                  id="newsletter-form-hire"
                  data-readdy-form=""
                  onSubmit={handleSubmit}
                  className="flex gap-2"
                >
                  <input
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Your email"
                    required
                    className="flex-1 min-w-0 px-4 py-3 text-sm bg-white border-0 rounded-l-full focus:outline-none focus:ring-2 focus:ring-[#F49729]/30 transition-all"
                  />
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-4 py-3 bg-[#F49729] text-white rounded-r-full hover:bg-[#e08820] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                  >
                    <i className="ri-arrow-right-line text-sm"></i>
                  </button>
                </form>
              )}
            </div>
          </div>

          <div className="pt-6 md:pt-8 border-t border-[#d9d0c5] flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-xs text-[#7F7F84]">&copy; 2026 GO TALENTS.</p>
          </div>
        </div>
      </div>
    </footer>
  );
}

// Main Page Component
export default function HireTalent() {
  return (
    <div className="min-h-screen bg-white font-['Inter',system-ui,sans-serif]">
      <Header />
      <main>
        <HeroSection />
        <ServicesCards />
        <CostSavings />
        <Exclusivity />
        <VideoSection />
        <Industries />
        <FinalCTA />
      </main>
      <Footer />
    </div>
  );
}
